import { Temporal } from "temporal-polyfill";
import type { VaultFile } from "../types";

export enum VaultFileStatusFilter {
  All = "all",
  Attached = "attached",
  NeedsReview = "needs-review",
  Processing = "processing",
  Unavailable = "unavailable",
}

export enum VaultFileSort {
  Newest = "newest",
  Oldest = "oldest",
}

export const ALL_VAULT_CATEGORIES = "all";
export const VAULT_FILES_PAGE_SIZE = 20;

export type VaultFileFilters = {
  search: string;
  category: string;
  status: VaultFileStatusFilter;
  sort: VaultFileSort;
};

export const INITIAL_VAULT_FILE_FILTERS: VaultFileFilters = {
  search: "",
  category: ALL_VAULT_CATEGORIES,
  status: VaultFileStatusFilter.All,
  sort: VaultFileSort.Newest,
};

export function hasVaultFileFilters(filters: VaultFileFilters): boolean {
  return (
    filters.search.trim() !== "" ||
    filters.category !== ALL_VAULT_CATEGORIES ||
    filters.status !== VaultFileStatusFilter.All
  );
}

function matchesStatus(
  file: VaultFile,
  status: VaultFileStatusFilter,
): boolean {
  switch (status) {
    case VaultFileStatusFilter.All:
      return true;
    case VaultFileStatusFilter.Attached:
      return file.linkedCredentials.length > 0;
    case VaultFileStatusFilter.NeedsReview:
      return file.isReviewDocument && file.reviewState !== "reading";
    case VaultFileStatusFilter.Processing:
      return file.reviewState === "reading";
    case VaultFileStatusFilter.Unavailable:
      return !file.canDownload;
  }
}

export function filterVaultFiles({
  files,
  filters,
}: {
  files: VaultFile[];
  filters: VaultFileFilters;
}): VaultFile[] {
  const search = filters.search.trim().toLocaleLowerCase();
  return files
    .filter((file) => {
      const searchable = [
        file.fileName,
        file.title,
        file.typeLabel,
        ...file.linkedCredentials.map((credential) => credential.label),
      ]
        .join(" ")
        .toLocaleLowerCase();
      return (
        searchable.includes(search) &&
        matchesStatus(file, filters.status) &&
        (filters.category === ALL_VAULT_CATEGORIES ||
          file.linkedCredentials.some(
            (credential) => credential.category === filters.category,
          ))
      );
    })
    .sort((left, right) => {
      const chronological = Temporal.Instant.compare(
        left.createdAt,
        right.createdAt,
      );
      const direction = filters.sort === VaultFileSort.Newest ? -1 : 1;
      return chronological * direction || left.id.localeCompare(right.id);
    });
}

const BYTES_PER_KIBIBYTE = 1024;
const BYTES_PER_MEBIBYTE = BYTES_PER_KIBIBYTE * BYTES_PER_KIBIBYTE;

export function vaultFileSize(size: number | null): string {
  if (size === null) {
    return "Size unavailable";
  }
  if (size < BYTES_PER_KIBIBYTE) {
    return `${size} B`;
  }
  if (size < BYTES_PER_MEBIBYTE) {
    return `${Math.ceil(size / BYTES_PER_KIBIBYTE)} KB`;
  }
  return `${(size / BYTES_PER_MEBIBYTE).toFixed(1)} MB`;
}
